import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { Stat } from "~/pages/manifold-nextgen/components/Stat";
import { EvalRunStatus } from "~/pages/manifold-nextgen/types/eval-run";
import { EVAL_SCORE_MAX } from "~/services/evals";
import { formatDuration, formatEvalTimestamp } from "./primitives/format";
import { parseApiTimestamp } from "./selectors/core";
import type { EvalKpis } from "./selectors/kpis";

/**
 * The change against the previous judged run. The arrow carries direction and the
 * tint carries sentiment, kept separate so a metric where up is bad only flips the
 * tint. `magnitude` is formatted by the caller, since the two measures round
 * differently and a component that guessed would misreport one.
 */
const DeltaValue = ({
  delta,
  magnitude,
  suffix,
}: {
  delta: number | null;
  magnitude: string;
  suffix: string;
}) => {
  if (delta == null) {
    return <span className="text-xs text-muted-foreground">no prior run</span>;
  }
  if (magnitude === "") {
    return (
      <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
        <Minus className="size-3" aria-hidden />
        unchanged
      </span>
    );
  }
  const isUp = delta > 0;
  const Icon = isUp ? ArrowUp : ArrowDown;
  return (
    <span
      className={cn(
        "flex items-center gap-0.5 text-xs font-medium",
        isUp ? "text-green-700" : "text-red-700"
      )}
    >
      <Icon className="size-3" aria-hidden />
      {magnitude} {suffix}
    </span>
  );
};

/** Whole percentage points, or "" when the change rounds away to nothing. */
const formatPointsDelta = (delta: number | null): string => {
  if (delta == null) {
    return "";
  }
  const rounded = Math.round(delta);
  return rounded === 0 ? "" : `${Math.abs(rounded)} pt${Math.abs(rounded) === 1 ? "" : "s"}`;
};

/** Two decimals on the 1-4 scale, or "" below the rubric's own jitter. */
const formatScoreDelta = (delta: number | null): string =>
  delta == null || Math.abs(delta) < 0.005 ? "" : Math.abs(delta).toFixed(2);

/** Five across at full width, so a tile fits a two-decimal score beside its delta. */
const KPI_ROW_GRID_CLS = "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5";

/**
 * A figure that has not arrived, sized to the line box of the text it stands in
 * for so the tile is already the height it will be. `block` rather than inline: an
 * inline box in a baseline-aligned row comes out 3px tall.
 */
const Pending = ({ className }: { className: string }) => (
  <Skeleton className={cn("block", className)} />
);

const PENDING_VALUE = <Pending className="h-9 w-16" />;
const PENDING_LINE = <Pending className="h-4 w-32" />;

/**
 * The numbers worth leading with, score first: a pass rate is the score already
 * thresholded, so it moves in coarse steps and says nothing about a suite drifting
 * from 3.9 to 3.2. It stays beside the score because a release gate reads it.
 *
 * Not a single hero figure — no one number answers "are the evals healthy".
 *
 * While the figures load only the figures wait: the labels are static copy, while
 * three of these five have a meaningful zero and would report a finding early.
 */
export const EvalKpiRow = ({ kpis, isLoading }: { kpis: EvalKpis; isLoading: boolean }) => {
  const passRate = kpis.latestSummary?.passRate;
  const startedAt = kpis.latestRun ? parseApiTimestamp(kpis.latestRun.started_at) : NaN;
  // A run still executing has only judged part of its suite, so both the figures
  // and the deltas against the previous run are provisional. Say so rather than
  // presenting a partial figure as the settled one.
  const isLatestInFlight =
    kpis.latestRun?.status === EvalRunStatus.RUNNING ||
    kpis.latestRun?.status === EvalRunStatus.PENDING;
  // A run still executing has judged only part of its suite, so its figures are
  // provisional and the deltas are suppressed. The tense carries the caveat.
  const latestDetail = kpis.latestRun
    ? `${isLatestInFlight ? "Running since" : "Ran on"} ${formatEvalTimestamp(startedAt)}`
    : "No judged runs yet";
  // A delta is suppressed for an in-flight run, and stands as a placeholder
  // while the figures it compares are still arriving.
  const delta = (node: React.ReactNode) =>
    isLoading ? <Pending className="h-4 w-28" /> : isLatestInFlight ? null : node;

  return (
    <div className={KPI_ROW_GRID_CLS}>
      <Stat
        label="Latest run (mean score)"
        value={
          isLoading
            ? PENDING_VALUE
            : kpis.latestMeanScore == null
              ? "—"
              : kpis.latestMeanScore.toFixed(2)
        }
        unit={isLoading || kpis.latestMeanScore == null ? undefined : ` / ${EVAL_SCORE_MAX}`}
        detail={isLoading ? PENDING_LINE : latestDetail}
      >
        {delta(
          <DeltaValue
            delta={kpis.scoreDelta}
            magnitude={formatScoreDelta(kpis.scoreDelta)}
            suffix="vs previous run"
          />
        )}
      </Stat>

      <Stat
        label="Latest run (pass rate)"
        value={
          isLoading ? PENDING_VALUE : passRate == null ? "—" : `${Math.round(passRate * 100)}%`
        }
        detail="Judged iterations that passed"
      >
        {delta(
          <DeltaValue
            delta={kpis.passRateDeltaPoints}
            magnitude={formatPointsDelta(kpis.passRateDeltaPoints)}
            suffix="vs previous run"
          />
        )}
      </Stat>

      {/* Broken and flaky sit side by side because they call for opposite work:
          a broken case points at the agent, a flaky one at the case. */}
      <Stat
        label="Flaky cases"
        value={isLoading ? PENDING_VALUE : String(kpis.flakyCaseCount)}
        detail="Score swings run to run"
      />

      <Stat
        label="Broken cases"
        value={isLoading ? PENDING_VALUE : String(kpis.brokenCaseCount)}
        // A finding, so it waits: read early it says the suite is clean when the
        // results have not arrived. "All judged cases…" rather than "Every judged
        // case…" because the row takes its height from the longest detail line.
        detail={
          isLoading
            ? PENDING_LINE
            : kpis.brokenCaseCount > 0
              ? "Never passed in its recent runs"
              : "All judged cases passed at least once"
        }
      />

      {/* Run wall clock, not case latency: the results list carries no per-case
          timing, so this is the only latency the page can aggregate honestly. */}
      <Stat
        label="Median run time"
        value={isLoading ? PENDING_VALUE : formatDuration(kpis.medianRunDurationSeconds)}
        detail="Wall clock, completed runs only"
      />
    </div>
  );
};
